import { orderRepository } from 'features/orders/data/repositories/OrderRepositoryImpl'
import { useCallback, useEffect, useState } from 'react'
import { paymentRepository } from '../../data/repositories/PaymentRepositoryImpl'
import { PaymentRecord, PaymentStatus } from '../../domain/entities/Payment'

export const usePayments = (initialLimit = 10) => {
  const [payments, setPayments] = useState<PaymentRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<PaymentStatus | undefined>(undefined) // Default to all payments (undefined)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const fetchPayments = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await paymentRepository.getPayments({
        search: search || undefined,
        status,
        page,
        limit: initialLimit,
      })
      setPayments(res.payments)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load payments.')
      setPayments([])
      setTotalPages(1)
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }, [search, status, page, initialLimit])

  useEffect(() => {
    fetchPayments()
  }, [fetchPayments])

  const approvePayment = async (id: string, orderId: string) => {
    setError(null)
    try {
      await paymentRepository.verifyPayment(id)

      // Advance the linked order the same way "Mark Payment Received" does on the
      // Orders page — best-effort, since the payment is already verified either way.
      try {
        const order = await orderRepository.getOrderById(orderId)
        if (order?.status === 'indent_raised') {
          await orderRepository.updateOrderStatus(order.id, 'payment_received', order.version)
        }
      } catch (orderSyncErr: unknown) {
        // NOSONAR: Order status sync logging for non-blocking error
        // eslint-disable-next-line no-console
        console.error('Failed to update order status', orderSyncErr) // NOSONAR
        // Non-blocking: order-status sync failure shouldn't undo the payment verification.
      }

      await fetchPayments()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to verify payment.')
      throw err
    }
  }

  const refusePayment = async (id: string, reason: string) => {
    setError(null)
    try {
      await paymentRepository.rejectPayment(id, reason)
      await fetchPayments()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to reject payment.')
      throw err
    }
  }

  const recordOffline = async (payment: Omit<PaymentRecord, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'buyer'>) => {
    setError(null)
    try {
      await paymentRepository.recordOfflinePayment(payment)
      await fetchPayments()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to record offline payment.')
      throw err
    }
  }

  const resetFilters = () => {
    setSearch('')
    setStatus(undefined)
    setPage(1)
  }

  return {
    payments,
    loading,
    error,
    search,
    setSearch,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    approvePayment,
    refusePayment,
    recordOffline,
    resetFilters,
    refresh: fetchPayments,
  }
}
